/**
 * Concordium Adapter for Reown AppKit
 * Aligns Concordium integration with the Bitcoin adapter patterns
 * to ensure consistent behaviour across non-EVM chains.
 */

import { ConstantsUtil as CommonConstantsUtil } from "@reown/appkit-common";
import {
  AdapterBlueprint,
  ChainController,
  ConnectionController,
  CoreHelperUtil,
  StorageUtil,
  WcHelpersUtil,
} from "@reown/appkit-controllers";
import { HelpersUtil } from "@reown/appkit-utils";
import { UserRejectedRequestError } from "viem";
import {
  ConcordiumGRPCWebClient,
  AccountAddress,
  CcdAmount,
} from "@concordium/web-sdk";
import * as CcdAmountModule from "@concordium/web-sdk/types/CcdAmount";
import {
  CCD_NAMESPACE,
  CCD_CURRENCY_SYMBOL,
  CCD_CURRENCY_DECIMALS,
  CCD_CHAIN_ID,
  CCD_RPC_URL,
} from "./ccdConstants.js";
import { ConcordiumWalletConnectConnector } from "./connectors/ConcordiumWalletConnectConnector.js";
import {
  isEvmStyleAddress,
  isLikelyConcordiumAddress,
  sessionHasCcdNamespace,
  sessionIsGenericOnly,
} from "./ccdSessionUtils.js";

export class ConcordiumAdapter extends AdapterBlueprint {
  constructor({ api = {}, ...params } = {}) {
    super({
      namespace: CCD_NAMESPACE,
      adapterType: "CONCORDIUM",
      ...params,
    });

    this.balancePromises = {};
    this.assetsCache = {};
    this.grpcClients = new Map();
    this.universalProvider = undefined;

    this.api = { ...api };
    if (typeof this.api.getAccountInfo !== "function") {
      this.api.getAccountInfo = async (args) => this.fetchGrpcAccountInfo(args);
    }
  }

  async connect(params) {
    let connector = this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(c.id, params.id)
    );

    if (!connector) {
      if (
        HelpersUtil.isLowerCaseMatch(
          params.id,
          CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
        ) &&
        this.universalProvider
      ) {
        connector = this.createWalletConnectConnector(this.universalProvider);
        this.addConnector(connector);
      } else {
        throw new Error(
          "connectionControllerClient:connectExternal - connector is undefined"
        );
      }
    }

    const chain =
      connector.chains?.find((c) => c.id === params.chainId) ||
      connector.chains?.[0] ||
      this.networks?.[0];

    if (!chain) {
      throw new Error(
        "The connector does not support any of the requested chains"
      );
    }

    const cachedConnection = this.connectionManager?.getConnection({
      address: params.address,
      connectorId: connector.id,
      connections: this.connections,
      connectors: this.connectors,
    });

    if (cachedConnection?.account) {
      this.emit("accountChanged", {
        address: cachedConnection.account.address,
        chainId: cachedConnection.caipNetwork?.id,
        connector,
      });
      this.ensureActiveConcordiumNetwork(chain, connector);
      return {
        id: connector.id,
        type: connector.type,
        address: cachedConnection.account.address,
        chainId: chain.id,
        provider: connector.provider,
      };
    }

    if (
      !HelpersUtil.isLowerCaseMatch(
        connector.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      )
    ) {
      try {
        await connector.connect();
      } catch (error) {
        throw new UserRejectedRequestError(error);
      }
    } else if (!connector.provider && this.universalProvider) {
      connector.provider = this.getWalletConnectProvider({
        provider: this.universalProvider,
        caipNetworks: this.getCaipNetworks(),
      });
    }

    const accounts = await this.getAccounts({ id: connector.id });
    const primaryAccount = accounts.accounts?.[0];

    if (!primaryAccount?.address) {
      throw new Error("Failed to get account address");
    }

    this.assertConcordiumWalletCompatible(connector, primaryAccount.address);

    this.emit("accountChanged", {
      address: primaryAccount.address,
      chainId: chain.id,
      connector,
    });

    this.addConnection({
      connectorId: connector.id,
      accounts: (accounts.accounts || []).map((account) => ({
        address: account.address,
        type: account.type || "default",
        publicKey: account.publicKey || "",
        path: account.path || "",
      })),
      caipNetwork: chain,
    });

    this.ensureActiveConcordiumNetwork(chain, connector);

    if (
      !HelpersUtil.isLowerCaseMatch(
        connector.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      )
    ) {
      this.listenProviderEvents(connector.id, connector);
    }

    return {
      id: connector.id,
      type: connector.type,
      address: primaryAccount.address,
      chainId: chain.id,
      provider: connector.provider,
    };
  }

  getWalletConnectConnector() {
    const wcConnectorId = CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT;
    return this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(c.id, wcConnectorId)
    );
  }

  getWalletConnectProviderInstance() {
    const connector = this.getWalletConnectConnector();
    return connector?.provider || this.universalProvider;
  }

  wcSessionSupportsConcordium() {
    const provider = this.getWalletConnectProviderInstance();
    if (!provider?.session) {
      return false;
    }
    const connector = this.getWalletConnectConnector();
    if (connector?.hasCcdNamespace) {
      return connector.hasCcdNamespace();
    }
    return sessionHasCcdNamespace(provider);
  }

  assertConcordiumWalletCompatible(connector, address) {
    const isWc = HelpersUtil.isLowerCaseMatch(
      connector?.id,
      CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
    );
    if (!isWc) {
      return;
    }

    const provider = connector.provider || this.universalProvider;
    if (sessionIsGenericOnly(provider) || !sessionHasCcdNamespace(provider)) {
      throw new Error(
        "This wallet does not support the Concordium network. Please connect with Concordium Wallet or a WalletConnect-compatible Concordium wallet."
      );
    }

    if (isEvmStyleAddress(address) || !isLikelyConcordiumAddress(address)) {
      throw new Error(
        "Connected account is not a Concordium address. Use a wallet that supports Concordium."
      );
    }
  }

  async getAccounts(params) {
    const connector = this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(c.id, params.id)
    );

    if (!connector) {
      return { accounts: [] };
    }

    const getAccountAddresses =
      connector.getAccountAddresses || connector.provider?.getAccountAddresses;

    if (typeof getAccountAddresses !== "function") {
      return { accounts: [] };
    }

    let addresses = [];
    try {
      addresses = await getAccountAddresses.call(
        connector.provider || connector
      );
    } catch {
      addresses = [];
    }

    const accounts = (addresses || []).map((entry) => {
      const account = CoreHelperUtil.getAccount(entry);
      return CoreHelperUtil.createAccount(
        CCD_NAMESPACE,
        account.address,
        account.type || "default",
        account.publicKey || "",
        account.path || ""
      );
    });

    return { accounts };
  }

  async syncConnectors(_options, _appKit) {
    if (this.universalProvider && !this.hasWalletConnectConnector()) {
      this.addConnector(
        this.createWalletConnectConnector(this.universalProvider)
      );
    }
    return Promise.resolve();
  }

  hasWalletConnectConnector() {
    return this.connectors.some((connector) =>
      HelpersUtil.isLowerCaseMatch(
        connector.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      )
    );
  }

  createWalletConnectConnector(
    provider,
    caipNetworks = this.getCaipNetworks()
  ) {
    return new ConcordiumWalletConnectConnector({
      provider,
      chains: caipNetworks,
      getActiveChain: () => {
        if (
          !ChainController ||
          typeof ChainController.getCaipNetworkByNamespace !== "function"
        ) {
          return null;
        }
        return ChainController.getCaipNetworkByNamespace(this.namespace);
      },
    });
  }

  syncConnection(params) {
    return this.connect({
      id: params.id,
      chainId: params.chainId,
      type: "",
    });
  }

  async onConnect(accounts, connectorId) {
    if (accounts?.length) {
      await this.onAccountsChanged(accounts, connectorId, false);
    }
  }

  async onAccountsChanged(
    accounts,
    connectorId,
    disconnectIfNoAccounts = true
  ) {
    if (!accounts?.length) {
      if (disconnectIfNoAccounts) {
        await this.disconnect({
          id: connectorId,
          fullTeardown: false,
        });
        this.ensureEvmNamespace();
      }
      return;
    }

    let normalizedAccounts = [];

    try {
      const allAccounts = await this.getAccounts({ id: connectorId });
      normalizedAccounts = allAccounts.accounts || [];
    } catch {
      normalizedAccounts = [];
    }

    if (normalizedAccounts.length === 0) {
      normalizedAccounts = accounts.map((account) => {
        const parsed = CoreHelperUtil.getAccount(account);
        return {
          address: parsed.address,
          type: parsed.type || "default",
          publicKey: parsed.publicKey || "",
          path: parsed.path || "",
        };
      });
    }

    const firstAccount = normalizedAccounts[0];
    if (!firstAccount?.address) {
      return;
    }

    const connector = this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(c.id, connectorId)
    );

    try {
      this.assertConcordiumWalletCompatible(connector, firstAccount.address);
    } catch (error) {
      await this.disconnect({ id: connectorId, fullTeardown: false });
      this.ensureEvmNamespace();
      console.warn("ConcordiumAdapter: incompatible wallet account", error);
      return;
    }

    const existingConnection = this.connectionManager?.getConnection({
      connectorId,
      connections: this.connections,
      connectors: this.connectors,
    });

    const fallbackNetwork = connector?.chains?.[0] || this.networks?.[0];
    const caipNetwork =
      existingConnection?.caipNetwork?.chainNamespace === this.namespace
        ? existingConnection.caipNetwork
        : fallbackNetwork;

    if (
      HelpersUtil.isLowerCaseMatch(
        this.getConnectorId(this.namespace),
        connectorId
      )
    ) {
      this.emit("accountChanged", {
        address: firstAccount.address,
        chainId: caipNetwork?.id,
        caipNetworkId: caipNetwork?.caipNetworkId,
        connector,
      });
    }

    this.addConnection({
      connectorId,
      accounts: normalizedAccounts.map((account) => ({
        address: account.address,
        type: account.type || "default",
        publicKey: account.publicKey || "",
        path: account.path || "",
      })),
      caipNetwork,
    });

    this.ensureActiveConcordiumNetwork(caipNetwork, connector);
    this.emitFirstAvailableConnection();
  }

  async syncConnections({
    connectToFirstConnector,
    caipNetwork,
    getConnectorStorageInfo,
  }) {
    await this.connectionManager?.syncConnections({
      connectors: this.connectors,
      caipNetwork,
      caipNetworks: this.getCaipNetworks(),
      universalProvider: this.universalProvider,
      onConnection: this.addConnection.bind(this),
      onListenProvider: this.listenProviderEvents.bind(this),
      getConnectionStatusInfo: getConnectorStorageInfo,
    });

    if (connectToFirstConnector) {
      this.emitFirstAvailableConnection();
    }
  }

  async disconnect(params) {
    if (params?.id) {
      const connector = this.connectors.find((c) =>
        HelpersUtil.isLowerCaseMatch(c.id, params.id)
      );

      if (!connector) {
        throw new Error("ConcordiumAdapter:disconnect - connector not found");
      }

      if (
        !connector.provider &&
        HelpersUtil.isLowerCaseMatch(
          connector.id,
          CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
        ) &&
        this.universalProvider
      ) {
        connector.provider = this.getWalletConnectProvider({
          provider: this.universalProvider,
          caipNetworks: this.getCaipNetworks(),
        });
      }

      const fullTeardown = params?.fullTeardown === true;
      const canDisconnectProvider =
        connector.provider &&
        typeof connector.provider.disconnect === "function";

      if (!canDisconnectProvider && fullTeardown) {
        throw new Error(
          "ConcordiumAdapter:disconnect - connector.provider is undefined"
        );
      }

      const connection = this.connectionManager?.getConnection({
        connectorId: connector.id,
        connections: this.connections,
        connectors: this.connectors,
      });

      if (fullTeardown && canDisconnectProvider) {
        try {
          await connector.provider.disconnect();
        } catch (error) {
          console.error(
            "ConcordiumAdapter: Failed to disconnect connector provider",
            error
          );
        }
      }

      this.removeProviderListeners(connector.id);
      this.deleteConnection(connector.id);
      this.clearCachedBalancesForConnection(connection);

      if (this.connections.length === 0) {
        this.emit("disconnect");
        this.balancePromises = {};
        this.assetsCache = {};

        if (fullTeardown) {
          await this.clearCcdNamespaceFromStorage();
          this.universalProvider = undefined;
          this.connectionManager?.reset?.();
          try {
            ConnectionController.resetWcConnection?.();
          } catch (error) {
            console.error(
              "ConcordiumAdapter: Failed to reset WalletConnect connection manager",
              error
            );
          }
        } else {
          try {
            ConnectionController.resetWcConnection?.();
          } catch (error) {
            console.error(
              "ConcordiumAdapter: Failed to reset WalletConnect UI state",
              error
            );
          }
        }

        this.ensureEvmNamespace();
      } else {
        this.emitFirstAvailableConnection();
      }

      return { connections: connection ? [connection] : [] };
    }

    return this.disconnectAll({ fullTeardown: params?.fullTeardown === true });
  }

  async onDisconnect(connectorId, options = {}) {
    const fullTeardown = options.fullTeardown !== false;
    await this.disconnect({ id: connectorId, fullTeardown });
  }

  async disconnectAll(params = {}) {
    const fullTeardown = params?.fullTeardown === true;
    const connections = await Promise.all(
      this.connections.map(async (connection) => {
        const connector = this.connectors.find((c) =>
          HelpersUtil.isLowerCaseMatch(c.id, connection.connectorId)
        );
        if (!connector) {
          return connection;
        }
        try {
          await this.disconnect({ id: connector.id, fullTeardown });
        } catch {
          // Intentionally swallow – continue disconnecting other connectors
        }
        return connection;
      })
    );
    return { connections };
  }

  async signMessage(params) {
    const connector = params.provider;
    if (!connector) {
      throw new Error("ConcordiumAdapter:signMessage - connector is undefined");
    }
    const signature = await connector.signMessage({
      message: params.message,
      address: params.address,
    });
    return { signature };
  }

  getWalletConnectProvider(params) {
    return this.createWalletConnectConnector(
      params.provider,
      params.caipNetworks
    );
  }

  async getBalance(params) {
    const { address, caipNetwork } = params;

    if (
      !address ||
      !caipNetwork ||
      caipNetwork.chainNamespace !== this.namespace
    ) {
      return { balance: "0.00", symbol: CCD_CURRENCY_SYMBOL };
    }

    const caipAddress = `${caipNetwork.caipNetworkId}:${address}`;

    const cachedBalance =
      typeof StorageUtil.getNativeBalanceCacheForCaipAddress === "function"
        ? StorageUtil.getNativeBalanceCacheForCaipAddress(caipAddress)
        : undefined;

    if (cachedBalance?.balance && cachedBalance?.symbol) {
      return {
        balance: cachedBalance.balance,
        symbol: cachedBalance.symbol,
      };
    }

    if (this.balancePromises[caipAddress]) {
      return this.balancePromises[caipAddress];
    }

    const balancePromise = this.fetchNativeBalance(address, caipNetwork)
      .then(({ balance, symbol, assets }) => {
        if (typeof StorageUtil.updateNativeBalanceCache === "function") {
          StorageUtil.updateNativeBalanceCache({
            caipAddress,
            balance,
            symbol,
            timestamp: Date.now(),
          });
        }
        this.assetsCache[caipAddress] = assets;
        return { balance, symbol };
      })
      .catch((error) => {
        console.error("ConcordiumAdapter: Failed to fetch balance", error);
        return { balance: "0.00", symbol: CCD_CURRENCY_SYMBOL };
      });

    this.balancePromises[caipAddress] = balancePromise;
    balancePromise.finally(() => {
      delete this.balancePromises[caipAddress];
    });

    return balancePromise;
  }

  async fetchNativeBalance(address, caipNetwork) {
    const symbol = caipNetwork?.nativeCurrency?.symbol || CCD_CURRENCY_SYMBOL;

    try {
      const accountInfo = await this.callApiGetAccountInfo(
        address,
        caipNetwork
      );

      if (!accountInfo?.accountAmount) {
        return { balance: "0.00", symbol, assets: [] };
      }

      const ccdBalance = CcdAmountModule.toCcd(accountInfo.accountAmount);
      const formattedBalance = ccdBalance.toFixed(CCD_CURRENCY_DECIMALS);

      const tokens = (accountInfo.accountTokens || []).map((token) => ({
        type: "token",
        symbol: token.id?.value || token.id,
        balance: this.formatTokenBalance(
          token.state.balance.value,
          token.state.balance.decimals || 0
        ),
        decimals: token.state.balance.decimals || 0,
      }));

      const assets = [
        {
          type: "native",
          symbol,
          balance: formattedBalance,
          decimals: CCD_CURRENCY_DECIMALS,
        },
        ...tokens,
      ];

      return { balance: formattedBalance, symbol, assets };
    } catch (error) {
      console.error(
        "ConcordiumAdapter: Failed to fetch Concordium balance",
        error
      );
      return { balance: "0.00", symbol, assets: [] };
    }
  }

  ensureEvmNamespace() {
    try {
      const evmNamespace = CommonConstantsUtil.CHAIN.EVM;
      const networks = ChainController.getCaipNetworks?.(evmNamespace);
      if (!networks?.length) {
        return;
      }
      const activeNamespace = ChainController.state?.activeChain;
      const activeNetwork = ChainController.state?.activeCaipNetwork;
      const needsSwitch =
        activeNamespace !== evmNamespace ||
        !activeNetwork ||
        activeNetwork.chainNamespace !== evmNamespace;
      if (needsSwitch) {
        ChainController.setActiveNamespace?.(evmNamespace);
        ChainController.setActiveCaipNetwork?.(networks[0]);
      }
    } catch (error) {
      console.error(
        "ConcordiumAdapter: Failed to ensure EVM namespace is active",
        error
      );
    }
  }

  formatTokenBalance(value, decimals) {
    try {
      const parsedValue =
        typeof value === "bigint" ? value : BigInt(value ?? 0);
      const divisor = BigInt(10) ** BigInt(decimals);
      const integer =
        divisor === BigInt(0) ? parsedValue : parsedValue / divisor;
      const fraction =
        divisor === BigInt(0) ? BigInt(0) : parsedValue % divisor;

      if (decimals === 0) {
        return integer.toString();
      }

      const fractionString = fraction
        .toString()
        .padStart(decimals, "0")
        .replace(/0+$/, "");

      return fractionString
        ? `${integer.toString()}.${fractionString}`
        : integer.toString();
    } catch {
      return "0";
    }
  }

  async callApiGetAccountInfo(address, caipNetwork) {
    const handler = this.api?.getAccountInfo;
    if (typeof handler !== "function") {
      return this.fetchGrpcAccountInfo({ address, caipNetwork });
    }
    try {
      return await handler({
        address,
        caipNetwork,
        getGrpcClient: () => this.ensureGrpcClient(caipNetwork),
      });
    } catch (primaryError) {
      try {
        return await handler(address, caipNetwork);
      } catch {
        console.error(
          "ConcordiumAdapter: custom getAccountInfo failed, falling back to gRPC",
          primaryError
        );
        return this.fetchGrpcAccountInfo({ address, caipNetwork });
      }
    }
  }

  async fetchGrpcAccountInfo(args) {
    const { address, caipNetwork } =
      typeof args === "string" ? { address: args } : args || {};

    if (!address) {
      return undefined;
    }

    const client = await this.ensureGrpcClient(caipNetwork);
    if (!client) {
      return undefined;
    }

    try {
      const accountAddress = AccountAddress.fromBase58(address);
      return await client.getAccountInfo(accountAddress);
    } catch (error) {
      console.error("ConcordiumAdapter: gRPC account fetch failed", error);
      return undefined;
    }
  }

  async ensureGrpcClient(caipNetwork) {
    const effectiveNetwork = caipNetwork || this.networks?.[0];
    const key = effectiveNetwork?.caipNetworkId || "default";

    if (this.grpcClients.has(key)) {
      return this.grpcClients.get(key);
    }

    const rpcUrl =
      effectiveNetwork?.rpcUrl ||
      effectiveNetwork?.rpcUrls?.default?.http?.[0] ||
      CCD_RPC_URL;

    try {
      const url = new URL(rpcUrl);
      const protocol = url.protocol || "https:";
      const host = url.hostname;
      const port = Number(url.port) || (protocol === "http:" ? 80 : 443);
      const baseUrl = `${protocol}//${host}`;
      const client = new ConcordiumGRPCWebClient(baseUrl, port);
      this.grpcClients.set(key, client);
      return client;
    } catch (error) {
      console.error(
        "ConcordiumAdapter: Failed to initialize Concordium GRPC client",
        error
      );
      return undefined;
    }
  }

  parseUnits({ amount, decimals = CCD_CURRENCY_DECIMALS }) {
    if (amount === undefined || amount === null) {
      throw new Error("Amount is required");
    }
    const stringValue = amount.toString().trim();
    const [integerPart, fractionPart = ""] = stringValue.split(".");
    const normalizedInteger = integerPart.replace(/\D/g, "") || "0";
    const normalizedFraction = fractionPart
      .replace(/\D/g, "")
      .slice(0, decimals)
      .padEnd(decimals, "0");
    return BigInt(`${normalizedInteger}${normalizedFraction}`);
  }

  formatUnits({ value, decimals = CCD_CURRENCY_DECIMALS }) {
    const bigintValue = typeof value === "bigint" ? value : BigInt(value);
    const divisor = BigInt(10) ** BigInt(decimals);
    const quotient = bigintValue / divisor;
    const remainder = bigintValue % divisor;

    if (remainder === BigInt(0) || decimals === 0) {
      return quotient.toString();
    }

    const fraction = remainder
      .toString()
      .padStart(decimals, "0")
      .replace(/0+$/, "");

    return fraction
      ? `${quotient.toString()}.${fraction}`
      : quotient.toString();
  }

  sendTransaction(params) {
    const { currency } = params;
    if (!currency) {
      throw new Error("Currency is required");
    }
    if (currency === CCD_CURRENCY_SYMBOL) {
      return this.sendCCD(params);
    }
    if (["EURR", "USDR"].includes(currency)) {
      return this.sendPltTransaction(params);
    }
    throw new Error("Currency is not supported");
  }

  async sendCCD(params) {
    const { to: toAccount, value, currency } = params;
    if (!currency || currency !== CCD_CURRENCY_SYMBOL) {
      throw new Error("Currency is not supported");
    }
    if (!toAccount) {
      throw new Error("Transaction recipient address (to) is required");
    }
    if (value === undefined || value === null) {
      throw new Error("Amount is required");
    }

    const { connector } = this.resolveActiveConnector();
    if (!connector) {
      throw new Error(
        "Concordium connector not found. Please connect your wallet first."
      );
    }

    await this.ensureConnectorProvider(connector);

    if (typeof connector.sendTransaction !== "function") {
      throw new Error("Connector does not support sendTransaction.");
    }

    const microCcdAmount = this.parseUnits({
      amount: value,
      decimals: CCD_CURRENCY_DECIMALS,
    });

    const transactionPayload = {
      amount: CcdAmount.fromMicroCcd(microCcdAmount.toString()),
      toAddress: AccountAddress.fromBase58(toAccount),
    };

    const hash = await connector.sendTransaction({
      transaction: transactionPayload,
    });

    if (!hash) {
      throw new Error("Transaction failed: No transaction hash returned");
    }

    return { hash };
  }

  async sendPltTransaction(params) {
    const { to: toAccount, value, currency, decimals, memo } = params;
    if (!currency || !["EURR", "USDR"].includes(currency)) {
      throw new Error("Currency is not supported");
    }
    if (!toAccount) {
      throw new Error("Transaction recipient address (to) is required");
    }
    if (value === undefined || value === null) {
      throw new Error("Amount is required");
    }

    const { connector } = this.resolveActiveConnector();
    if (!connector) {
      throw new Error(
        "Concordium connector not found. Please connect your wallet first."
      );
    }

    await this.ensureConnectorProvider(connector);

    if (typeof connector.sendPltTransaction !== "function") {
      throw new Error("Connector does not support sendPltTransaction.");
    }

    const payload = {
      amount: value,
      toAddress: toAccount,
      tokenId: currency,
      ...(decimals !== undefined ? { decimals } : {}),
      ...(memo ? { memo } : {}),
    };

    const hash = await connector.sendPltTransaction({
      transaction: payload,
    });

    if (!hash) {
      throw new Error("Transaction failed: No transaction hash returned");
    }

    return { hash };
  }

  async estimateGas(_params) {
    return Promise.resolve({ gas: BigInt(0) });
  }

  async writeContract(_params) {
    throw new Error("writeContract is not supported for Concordium");
  }

  async grantPermissions(_params) {
    return Promise.resolve({});
  }

  async getCapabilities(_params) {
    return Promise.resolve({});
  }

  async revokePermissions(_params) {
    return Promise.resolve("0x0");
  }

  async walletGetAssets(params = {}) {
    const { address, caipNetwork } = params;
    if (
      !address ||
      !caipNetwork ||
      caipNetwork.chainNamespace !== this.namespace
    ) {
      return { assets: [] };
    }
    const caipAddress = `${caipNetwork.caipNetworkId}:${address}`;
    if (!this.assetsCache[caipAddress]) {
      await this.getBalance({ address, caipNetwork });
    }
    return { assets: this.assetsCache[caipAddress] || [] };
  }

  async onChainChanged(chainId, connectorId) {
    const connector = this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(c.id, connectorId)
    );

    if (!connector) {
      throw new Error(
        "ConcordiumAdapter:onChainChanged - connector is undefined"
      );
    }

    const isWc = HelpersUtil.isLowerCaseMatch(
      connector.id,
      CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
    );

    if (isWc && !this.wcSessionSupportsConcordium()) {
      await this.disconnect({ id: connector.id, fullTeardown: false });
      this.ensureEvmNamespace();
      return;
    }

    let address;
    try {
      const result = await this.connect({
        id: connector.id,
        chainId,
        type: "",
      });
      address = result.address;
    } catch (error) {
      await this.disconnect({ id: connector.id, fullTeardown: false });
      this.ensureEvmNamespace();
      console.warn("ConcordiumAdapter:onChainChanged - connect failed", error);
      return;
    }

    const accounts = await this.getAccounts({ id: connector.id });
    const chain =
      connector.chains?.find((c) => c.id === chainId) ||
      connector.chains?.[0] ||
      this.networks?.[0];

    if (
      HelpersUtil.isLowerCaseMatch(
        this.getConnectorId(this.namespace),
        connector.id
      )
    ) {
      this.emit("switchNetwork", { chainId, address });
    }

    this.addConnection({
      connectorId: connector.id,
      accounts: (accounts.accounts || []).map((account) => ({
        address: account.address,
        type: account.type || "default",
        publicKey: account.publicKey || "",
        path: account.path || "",
      })),
      caipNetwork: chain,
    });

    this.ensureActiveConcordiumNetwork(chain, connector);
  }

  setAuthProvider() {
    return undefined;
  }

  async setUniversalProvider(universalProvider) {
    this.universalProvider = universalProvider;
    const wcConnectorId = CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT;

    WcHelpersUtil.listenWcProvider({
      universalProvider,
      namespace: CCD_NAMESPACE,
      onConnect: (accounts) => this.onConnect(accounts, wcConnectorId),
      onDisconnect: () =>
        this.onDisconnect(wcConnectorId, { fullTeardown: true }),
      onAccountsChanged: (accounts) =>
        this.onAccountsChanged(accounts, wcConnectorId, false),
    });

    this.addOrReplaceWalletConnectConnector(universalProvider);
    return Promise.resolve();
  }

  addOrReplaceWalletConnectConnector(provider) {
    const wcConnectorId = CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT;
    const existingIndex = this.connectors.findIndex((connector) =>
      HelpersUtil.isLowerCaseMatch(connector.id, wcConnectorId)
    );
    const instance = this.createWalletConnectConnector(provider);
    if (existingIndex !== -1) {
      this.connectors.splice(existingIndex, 1);
    }
    this.addConnector(instance);
  }

  shouldActivateConcordiumNamespace(connector) {
    const isWc =
      connector &&
      HelpersUtil.isLowerCaseMatch(
        connector.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      );
    if (!isWc) {
      return true;
    }
    return this.wcSessionSupportsConcordium();
  }

  ensureActiveConcordiumNetwork(caipNetwork, connector) {
    if (!ChainController) {
      return;
    }
    const wcConnector =
      connector || this.getWalletConnectConnector() || undefined;
    if (!this.shouldActivateConcordiumNamespace(wcConnector)) {
      this.ensureEvmNamespace();
      return;
    }
    try {
      if (typeof ChainController.setActiveNamespace === "function") {
        ChainController.setActiveNamespace(this.namespace);
      }
      if (
        caipNetwork &&
        typeof ChainController.setActiveCaipNetwork === "function"
      ) {
        ChainController.setActiveCaipNetwork(caipNetwork);
      }
    } catch {
      // Silently ignore – network activation is a best-effort operation.
    }
  }

  async recoverFromStuckState() {
    try {
      const provider = this.getWalletConnectProviderInstance();
      const activeNamespace = ChainController?.state?.activeChain;
      const activeNetwork = ChainController?.state?.activeCaipNetwork;
      const onConcordiumNamespace =
        activeNamespace === this.namespace ||
        activeNetwork?.chainNamespace === this.namespace;

      const genericOnly = sessionIsGenericOnly(provider);
      const hasCcd = sessionHasCcdNamespace(provider);
      const brokenSession =
        Boolean(provider) && (!provider.session || !provider.session.topic);

      const orphanConcordiumConnection = this.connections.some(
        (connection) =>
          connection.caipNetwork?.chainNamespace === this.namespace
      );

      const needsRecovery =
        brokenSession ||
        (onConcordiumNamespace && !hasCcd) ||
        (orphanConcordiumConnection && !hasCcd) ||
        (genericOnly && onConcordiumNamespace) ||
        (!this.universalProvider && orphanConcordiumConnection);

      if (!needsRecovery) {
        return { recovered: false };
      }

      const wcConnectorId = CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT;
      if (this.connections.length > 0) {
        await this.disconnect({
          id: wcConnectorId,
          fullTeardown: false,
        }).catch(() => {});
      } else if (onConcordiumNamespace && !hasCcd) {
        this.ensureEvmNamespace();
        try {
          ConnectionController.resetWcConnection?.();
        } catch {
          // ignore
        }
      }

      if (onConcordiumNamespace && !hasCcd && this.connections.length === 0) {
        this.ensureEvmNamespace();
      }

      if (
        brokenSession &&
        provider &&
        typeof provider.disconnect === "function"
      ) {
        try {
          await provider.disconnect();
        } catch {
          // Session may already be dead
        }
      }

      if (needsRecovery) {
        try {
          ConnectionController.resetWcConnection?.();
        } catch (error) {
          console.error(
            "ConcordiumAdapter: recoverFromStuckState reset failed",
            error
          );
        }
      }

      return { recovered: true };
    } catch (error) {
      console.error("ConcordiumAdapter: recoverFromStuckState failed", error);
      return { recovered: false, error };
    }
  }

  async clearCcdNamespaceFromStorage() {
    const provider = this.universalProvider;
    if (!provider) {
      return;
    }

    const deleteNamespace = (container) => {
      if (!container) {
        return false;
      }
      let mutated = false;
      if (container[CCD_NAMESPACE]) {
        delete container[CCD_NAMESPACE];
        mutated = true;
      }
      const compositeKey = `${CCD_NAMESPACE}:${CCD_CHAIN_ID}`;
      if (container[compositeKey]) {
        delete container[compositeKey];
        mutated = true;
      }
      return mutated;
    };

    deleteNamespace(provider.namespaces);
    deleteNamespace(provider.optionalNamespaces);
    deleteNamespace(provider.session?.namespaces);
    deleteNamespace(provider.session?.optionalNamespaces);

    const storage = provider.client?.core?.storage;
    if (!storage) {
      return;
    }

    const topic = provider.session?.topic || "";
    const storagePrefix = "wc@2:universal_provider:/";
    const targetKeys = ["namespaces", "optionalNamespaces"];

    for (const key of targetKeys) {
      const storageKey = `${storagePrefix}${key}${topic}`;
      try {
        const value = await storage.getItem(storageKey);
        if (!value) {
          continue;
        }
        if (Array.isArray(value)) {
          const filtered = value.filter(
            (entry) =>
              entry !== CCD_NAMESPACE &&
              entry !== `${CCD_NAMESPACE}:${CCD_CHAIN_ID}`
          );
          if (filtered.length !== value.length) {
            if (filtered.length === 0) {
              await storage.removeItem(storageKey);
            } else {
              await storage.setItem(storageKey, filtered);
            }
          }
        } else if (typeof value === "object") {
          const cloned = { ...value };
          const mutated = deleteNamespace(cloned);
          if (mutated) {
            if (Object.keys(cloned).length === 0) {
              await storage.removeItem(storageKey);
            } else {
              await storage.setItem(storageKey, cloned);
            }
          }
        }
      } catch (error) {
        console.error(
          "ConcordiumAdapter: Failed to prune WalletConnect storage key",
          key,
          error
        );
      }
    }

    try {
      const signClient = provider.client;
      const session = topic ? signClient?.session?.get(topic) : undefined;
      if (!session) {
        return;
      }

      const updatedNamespaces = { ...(session.namespaces || {}) };
      const namespacesMutated = deleteNamespace(updatedNamespaces);

      const updatedOptionalNamespaces = {
        ...(session.optionalNamespaces || {}),
      };
      const optionalMutated = deleteNamespace(updatedOptionalNamespaces);

      if (namespacesMutated || optionalMutated) {
        const payload = {
          namespaces: updatedNamespaces,
          optionalNamespaces: optionalMutated
            ? Object.keys(updatedOptionalNamespaces).length > 0
              ? updatedOptionalNamespaces
              : undefined
            : session.optionalNamespaces,
          acknowledged:
            session.acknowledged !== false ? session.acknowledged : true,
        };

        await signClient.session.set(topic, {
          ...session,
          ...payload,
        });
      }
    } catch (error) {
      console.error(
        "ConcordiumAdapter: Failed to update SignClient session after CCD cleanup",
        error
      );
    }
  }

  resolveActiveConnector() {
    const activeConnection = this.connections.find(
      (connection) => connection.caipNetwork?.chainNamespace === this.namespace
    );
    if (activeConnection) {
      const connector = this.connectors.find((c) =>
        HelpersUtil.isLowerCaseMatch(c.id, activeConnection.connectorId)
      );
      if (connector) {
        return { connector, caipNetwork: activeConnection.caipNetwork };
      }
    }
    const walletConnect = this.connectors.find((c) =>
      HelpersUtil.isLowerCaseMatch(
        c.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      )
    );
    if (walletConnect) {
      return {
        connector: walletConnect,
        caipNetwork: walletConnect.chains?.[0] || this.networks?.[0],
      };
    }
    return { connector: undefined, caipNetwork: undefined };
  }

  async ensureConnectorProvider(connector) {
    if (
      HelpersUtil.isLowerCaseMatch(
        connector.id,
        CommonConstantsUtil.CONNECTOR_ID.WALLET_CONNECT
      ) &&
      !connector.provider &&
      this.universalProvider
    ) {
      connector.provider = this.getWalletConnectProvider({
        provider: this.universalProvider,
        caipNetworks: this.getCaipNetworks(),
      });
    }
  }

  clearCachedBalancesForConnection(connection) {
    if (!connection?.accounts || !connection.caipNetwork) {
      return;
    }
    const caipNetworkId = connection.caipNetwork.caipNetworkId;
    for (const account of connection.accounts) {
      if (!account?.address) {
        continue;
      }
      const caipAddress = `${caipNetworkId}:${account.address}`;
      delete this.balancePromises[caipAddress];
      delete this.assetsCache[caipAddress];
      if (
        typeof StorageUtil.removeAddressFromNativeBalanceCache === "function"
      ) {
        StorageUtil.removeAddressFromNativeBalanceCache(caipAddress);
      }
    }
  }
}
